/**
 * Streams a recording up to Google Cloud Storage as it is being recorded.
 *
 * Lifecycle:
 *   1. Construct with the active `BaseRecorder` (after `recorder.initialize()`,
 *      so `recorder.mimeType` is set).
 *   2. Call `start()` *before* `recorder.startRecording()` — this opens the
 *      resumable session and subscribes to the recorder's data-available
 *      events.
 *   3. Recorder runs as usual. Each blob produced by the MediaRecorder is
 *      forwarded to `ResumableUploadSession.appendBlob`, which serializes
 *      256 KiB-aligned PUTs to GCS.
 *   4. After `recorder.finishRecording()` resolves (the final `dataavailable`
 *      has fired), call `finalize()`. It drains the buffer, sends the final
 *      (size-known) chunk, and returns the GCS location + asyncOperationUuid
 *      ready to pass into `UploadMediaController._finalizeUploadedAsset`.
 *
 * If the user retakes mid-recording, call `abort()` — the listener detaches
 * and the partial GCS object is left for the bucket's lifecycle policy to
 * clean up.
 */
import { RECORDING_FILENAME_PREFIX } from 'js/src/data/uploads/uploads-types';
import type { BaseRecorder } from 'js/src/libs/recorder/base-recorder';
import { ResumableUploadSession } from 'js/src/libs/resumable-gcs-upload';
import uuid from 'js/src/libs/uuid';

type FinalizedRecording = {
    bucket: string;
    file_name: string;
    asyncOperationUuid: string;
    mimeType: string;
    fileName: string;
};

export class RecordingUploader {
    readonly #recorder: BaseRecorder;
    #session: ResumableUploadSession | null = null;
    #unsubscribe: (() => void) | null = null;
    #aborted = false;

    constructor(recorder: BaseRecorder) {
        this.#recorder = recorder;
    }

    /**
     * Open the resumable session and start forwarding MediaRecorder chunks to
     * GCS. Must be called *after* `recorder.initialize()` (so the mime type is
     * known) and *before* `recorder.startRecording()` (so we don't miss the
     * first chunk).
     */
    async start(): Promise<void> {
        if (this.#session) {
            throw new Error('RecordingUploader.start() called twice');
        }
        const mimeType = this.#recorder.mimeType;
        if (!mimeType) {
            throw new Error(
                'RecordingUploader.start() requires recorder.mimeType — call recorder.initialize() first',
            );
        }
        const fileExtension = mimeType.split('/').at(-1) ?? 'webm';
        const fileName = `${RECORDING_FILENAME_PREFIX}-${uuid()}.${fileExtension}`;

        const session = await ResumableUploadSession.create({ fileName, mimeType });
        // If abort() ran while we were awaiting the session-init API call,
        // drop the session on the floor — wiring up the listener would
        // re-arm an uploader the caller already abandoned.
        if (this.#aborted) {
            return;
        }
        this.#session = session;
        this.#unsubscribe = this.#recorder.onDataAvailable((blob: Blob) => {
            session.appendBlob(blob);
        });
    }

    /**
     * Drains the buffer and finalizes the GCS upload. Caller is expected to
     * await `recorder.finishRecording()` (or equivalent) first so all
     * `dataavailable` events have been delivered.
     */
    async finalize(): Promise<FinalizedRecording> {
        if (!this.#session) {
            throw new Error('RecordingUploader.finalize() called before start()');
        }
        this.#unsubscribe?.();
        this.#unsubscribe = null;

        const result = await this.#session.finalize();
        return {
            ...result,
            mimeType: this.#session.mimeType,
            fileName: this.#session.fileName,
        };
    }

    /**
     * Abandon the upload. The partial GCS object is left for the bucket's
     * lifecycle policy to clean up (configured via
     * `_add_lifecycle_abort_incomplete_uploads`).
     */
    abort(): void {
        this.#aborted = true;
        this.#unsubscribe?.();
        this.#unsubscribe = null;
        this.#session = null;
    }
}
